/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

// Component imports
import { Button, Empty, Modal, Typography } from 'antd';
import { FullscreenOutlined } from '@ant-design/icons';
import EchartsWrapper from './EchartsWrapper';
import ChartLoader from './ChartLoader';

// Misc imports
import is from '../../utils/is';
import { isEmptyArray } from '../../utils/is/isEmptyArray';
import useTheme from '../../utils/hooks/useTheme';
import { formatNumberToThousandSeparator } from '../../utils/string/formatNumberToThousandSeparator';

const CSATOvertimeResponsesSection = ({ data = {}, sectionTitle = '', seriesName = '', isLoading = true, renderOnlyChart = false, renderOnlyTable = false }) => {
  const [chartToFullscreen, setChartToFullscreen] = useState();
  const { t } = useTranslation();
  const { primaryColorAnalytics, primaryColorAnalyticsOpacity } = useTheme();

  const getOptions = (data) => {

    const answered = data?.histogram?.map((item) => ({
      value: item?.answered,
      itemStyle: item?.notAnswered === undefined || item?.notAnswered === 0
        ? { borderRadius: [6, 6, 0, 0] }
        : {}
    }));

    const presented = data?.histogram?.map(item => item?.presented || 0);
    const notAnswered = data?.histogram?.map(item => item?.notAnswered || 0);
    const dates = data?.histogram?.map(item => item?.date);

    return {
      grid: {
        left: '4%',
        right: '6%',
        bottom: '10%',
        top: '10%',
        containLabel: true
      },
      tooltip: {
        trigger: 'item',
        className: 'ant-tooltip p-0 border-none',
        formatter: (params) => {
          const date = dates[params.dataIndex];
          const responses = answered[params.dataIndex]?.value;
          const csatNotAnswered = notAnswered[params.dataIndex];
          const appearances = presented[params.dataIndex];

          return `
          <div 
          class="d-flex flex-column border-radius-base shadow"
          style="width: 220px; max-width: 220px;"
          >
          <div 
          class="bg-layout text-center p-2 border-radius-top-left-base border-radius-top-right-base fw-600 text-wrap"
          >
            ${date}
          </div>
          <div class="d-flex flex-column p-2 w-100">
          <div class="mb-1">
          <span>Total Appearances:</span>
            <span class="fw-600">${formatNumberToThousandSeparator(appearances)}</span>
          </div>
          <div class="mb-1">
          <span>Responses:</span>
            <span class="fw-600">${formatNumberToThousandSeparator(responses)}</span>
            </div>
            <div class="mb-1">
            <span>Not Answered:</span> 
            <span class="fw-600">${formatNumberToThousandSeparator(csatNotAnswered)}</span>
            </div>
          </div>
        </div>`;
        }
      },
      legend: {
        show: true,
        bottom: 0,
        textStyle: {
          rich: {
            count: {
              color: '#000',
              fontWeight: 'bold',
              fontSize: '24px'
            }
          }
        },
        formatter: (name) => {
          const parts = name.split(' ');
          const count = parts.pop();
          const label = parts.join(' ');

          return `${label}  {count|${formatNumberToThousandSeparator(count)}}   `;
        }
      },
      toolbox: {
        show: false
      },
      xAxis: [{
        type: 'category',
        position: 'bottom',
        axisTick: {
          show: true,
          alignWithLabel: true
        },
        axisLine: {
          show: false
        },
        axisLabel: {
          show: true,
          interval: dates?.length - 2
        },
        data: dates
      }],
      yAxis: [
        {
          type: 'value',
          position: 'left',
          alignTicks: true
        }
      ],
      series: [
        {
          name: `CSAT Responses ${data.totalAnswered || 0}`,
          type: 'bar',
          stack: 'total',
          itemStyle: {
            color: primaryColorAnalytics
          },
          data: answered
        },
        {
          name: `CSAT Appearances ${data.totalPresented || 0}`,
          type: 'bar',
          stack: 'total',
          itemStyle: {
            color: primaryColorAnalyticsOpacity,
            borderRadius: [6, 6, 0, 0]
          },
          data: notAnswered
        }
      ]
    };
  };

  const chart
    = (<Fragment>
      {
        !isEmptyArray(data.histogram)
          ? <EchartsWrapper
            chartOptions={getOptions(data)}
            externalClassname="d-flex justify-content-center"
            style={{ height: 367, minHeight: '367px' }}
            loading={isLoading}
          />
          : <div className="d-flex content-fill flex-column h-100">
            <div className="mt-5 d-flex align-items-center mb-auto text-center flex-auto align-self-center">
              <Empty
                description={(
                  <Typography.Paragraph>
                    {t('No data available for this chart')}
                  </Typography.Paragraph>
                )}
              />
            </div>
          </div>

      }
    </Fragment>
    );

  return (
    <Fragment>
      {
        renderOnlyChart ? chart
          : <Fragment>
            <div className="mb-6 position-relative">
              <Typography.Title level={4} className="mb-6">
                <Typography.Text className="mr-2">{sectionTitle}</Typography.Text>
              </Typography.Title>

              {isLoading ? <ChartLoader /> : chart}

              <Button
                type="text"
                size="large"
                disabled={isLoading}
                icon={<FullscreenOutlined />}
                className="position-absolute top-0 right-0"
                onClick={() => setChartToFullscreen(chart)}
              />
            </div>

            <Modal
              open={is.truthy(chartToFullscreen)}
              onCancel={() => setChartToFullscreen()}
              destroyOnClose
              footer={null}
              centered
              style={{ maxWidth: 1000 }}
              width={'95%'}
            >
              <div className="p-5">
                {chart}
              </div>
            </Modal>
          </Fragment>

      }
    </Fragment>
  );
};

CSATOvertimeResponsesSection.propTypes = {
  data: PropTypes.object,
  sectionTitle: PropTypes.string,
  seriesName: PropTypes.string,
  isLoading: PropTypes.bool,
  renderOnlyChart: PropTypes.bool,
  renderOnlyTable: PropTypes.bool
};

export default CSATOvertimeResponsesSection;
